import React, { useEffect, useState } from 'react'
import { Gem, Sparkles } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { WHEEL500_COLORS, WHEEL500_COLOR_KEYS, WHEEL500_PHASES } from '../mock/wheel500Mock'

/**
 * Лента ставок текущего раунда Wheel500.
 * Показывает аватар, имя, цвет, сумму. На фазе result подсвечивает win/lose.
 */

const MERGE_DELAY_MS = 700

const BETS_THEME = {
  esports: {
    wrapperClass: 'rounded-2xl border border-cyan-400/20 bg-slate-950/45 p-3 shadow-[0_10px_26px_rgba(6,182,212,0.12)]',
    titleClass: 'text-cyan-100',
    counterClass: 'text-cyan-200/85',
    columnCardClass: 'backdrop-blur-sm',
    emptyClass: 'text-cyan-200/65'
  },
  casino: {
    wrapperClass: 'rounded-2xl border border-amber-300/20 bg-gradient-to-b from-amber-500/10 via-rose-500/5 to-transparent p-3 shadow-[0_10px_28px_rgba(245,158,11,0.12)]',
    titleClass: 'text-amber-100',
    counterClass: 'text-amber-200/85',
    columnCardClass: 'shadow-[0_4px_18px_rgba(245,158,11,0.08)]',
    emptyClass: 'text-amber-200/70'
  },
  minimal: {
    wrapperClass: 'rounded-2xl border border-white/10 bg-surface/50 p-3',
    titleClass: 'text-white',
    counterClass: 'text-text-secondary',
    columnCardClass: '',
    emptyClass: 'text-text-secondary'
  }
}

const getItemsTotal = (items = []) => {
  return items.reduce((acc, item) => acc + Number(item?.cost || item?.price || 0), 0)
}

const AnimatedBetItems = ({ betItems, winSkin, isWinner, multiplier }) => {
  const [isMerged, setIsMerged] = useState(false)
  const items = Array.isArray(betItems) ? betItems.slice(0, 6) : []

  useEffect(() => {
    if (!isWinner || !winSkin || items.length === 0) {
      setIsMerged(false)
      return
    }

    const timer = setTimeout(() => {
      setIsMerged(true)
    }, MERGE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [isWinner, winSkin, items.length])

  if (items.length === 0 && !isMerged) {
    return null
  }

  const itemsTotal = getItemsTotal(items)

  return (
    <div className="mt-1.5 rounded-md border border-white/10 bg-dark-primary/45 p-1.5">
      <AnimatePresence initial={false} mode="wait">
        {!isMerged ? (
          <motion.div
            key="base-items"
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98, filter: 'blur(2px)' }}
            transition={{ duration: 0.28 }}
          >
            <div className="mb-1 flex items-center justify-between text-[9px]">
              <span className="text-text-secondary">Скины: {items.length}</span>
              <span className="inline-flex items-center gap-0.5 text-accent-primary font-semibold">
                {itemsTotal.toLocaleString()} <Gem className="h-2.5 w-2.5" />
              </span>
            </div>

            <div className="flex items-center gap-1 overflow-hidden">
              {items.slice(0, 4).map((item, index) => (
                <motion.div
                  key={`${item.id || index}-item`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(index, 6) * 0.03 }}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-white/10 bg-dark-primary/70 text-sm"
                  title={`${item.name} · ${Number(item.cost || item.price || 0).toLocaleString()}`}
                >
                  {item.emoji || item.img || '🎲'}
                </motion.div>
              ))}

              {items.length > 4 && (
                <div className="flex h-7 min-w-7 shrink-0 items-center justify-center rounded border border-white/10 bg-dark-primary/70 px-1 text-[10px] text-text-secondary">
                  +{items.length - 4}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="merged-item"
            initial={{ opacity: 0, scale: 0.9, filter: 'blur(4px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-md border border-accent-primary/45 bg-accent-primary/12 p-1.5 shadow-[0_0_14px_rgba(0,255,127,0.25)]"
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-[9px] text-emerald-300 font-semibold">
                <Sparkles className="h-3 w-3" /> Слияние ×{multiplier}
              </span>
              <span className="text-[9px] text-text-secondary">{items.length} → 1</span>
            </div>

            <div className="flex items-center gap-1.5 rounded border border-white/10 bg-dark-primary/55 p-1.5">
              <div className="text-lg leading-none">{winSkin?.emoji || winSkin?.img || '💎'}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[10px] text-white font-semibold">{winSkin?.name || 'Merged Skin'}</p>
                <p className="inline-flex items-center gap-0.5 text-[10px] text-accent-primary font-bold">
                  {Number(winSkin?.cost || winSkin?.price || 0).toLocaleString()} <Gem className="h-2.5 w-2.5" />
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const Wheel500BetsFeed = ({ bets, status, winColor, designVariant = 'minimal' }) => {
  const isResult = status === WHEEL500_PHASES.RESULT
  const activeTheme = BETS_THEME[designVariant] || BETS_THEME.minimal
  const groupedBets = WHEEL500_COLOR_KEYS.reduce((acc, colorKey) => {
    acc[colorKey] = bets.filter((bet) => bet.color === colorKey).slice().reverse()
    return acc
  }, {})

  return (
    <div className={activeTheme.wrapperClass}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`text-sm font-semibold ${activeTheme.titleClass}`}>Ставки по цветам</h3>
        <span className={`text-xs ${activeTheme.counterClass}`}>{bets.length}</span>
      </div>

      {bets.length === 0 ? (
        <p className={`py-6 text-center text-xs ${activeTheme.emptyClass}`}>Пока никто не поставил</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {WHEEL500_COLOR_KEYS.map((colorKey) => {
            const colorCfg = WHEEL500_COLORS[colorKey]
            const columnBets = groupedBets[colorKey] || []
            const isWinColumn = isResult && winColor === colorKey
            const isLoseColumn = isResult && winColor && winColor !== colorKey
            const winColumnPayout = columnBets.reduce((acc, bet) => acc + Number(bet.payout || 0), 0)

            return (
              <div
                key={colorKey}
                className={`rounded-xl border p-2 transition-all ${colorCfg.softClass} ${activeTheme.columnCardClass} ${
                  isWinColumn ? `ring-2 ${colorCfg.ringClass} shadow-[0_0_22px_rgba(255,255,255,0.16)]` : ''
                } ${isLoseColumn ? 'opacity-55' : ''}`}
              >
                <div className="mb-1.5 flex items-center justify-between">
                  <span className={`text-xs font-bold ${colorCfg.textClass}`}>
                    {colorCfg.label} ×{colorCfg.multiplier}
                  </span>
                  <span className="text-[10px] text-white/80">{columnBets.length}</span>
                </div>

                {isWinColumn && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.28 }}
                    className="mb-1.5 rounded-md border border-emerald-300/40 bg-emerald-400/15 px-2 py-1"
                  >
                    <p className="text-[10px] font-semibold text-emerald-200">Выигрыш столбца</p>
                    <p className="inline-flex items-center gap-1 text-xs font-bold text-emerald-100">
                      +{winColumnPayout.toLocaleString()} <Gem className="h-3 w-3" />
                    </p>
                  </motion.div>
                )}

                {columnBets.length === 0 ? (
                  <p className="py-4 text-center text-[10px] text-text-secondary">Нет ставок</p>
                ) : (
                  <div className="max-h-[250px] space-y-1.5 overflow-y-auto pr-0.5">
                    {columnBets.map((bet) => {
                      const isWinBet = isResult && bet.color === winColor
                      const betItems = Array.isArray(bet.bet_items) ? bet.bet_items : []
                      const multiplier = Number(colorCfg?.multiplier || 1)

                      return (
                        <div
                          key={bet.id}
                          className={`rounded-md border border-white/10 bg-dark-primary/40 px-1.5 py-1.5 transition-colors ${
                            bet.isMine ? 'ring-1 ring-accent-primary/60' : ''
                          } ${isWinBet ? 'shadow-[0_0_14px_rgba(16,185,129,0.35)]' : ''}`}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm leading-none">{bet.user?.ava || '👤'}</span>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[11px] text-white font-medium">
                                {bet.isMine ? 'Вы' : bet.user?.name}
                              </p>
                              <p className="inline-flex items-center gap-1 text-[11px] text-white font-semibold">
                                {Number(bet.sum || bet.amount || 0).toLocaleString()} <Gem className="h-3 w-3 text-accent-primary" />
                              </p>
                            </div>
                          </div>

                          {isWinBet && (
                            <motion.p
                              initial={{ opacity: 0, y: 5, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ duration: 0.25 }}
                              className="mt-1 inline-flex items-center gap-1 rounded-full border border-emerald-300/40 bg-emerald-400/15 px-2 py-0.5 text-[10px] text-emerald-100 font-bold"
                            >
                              <Sparkles className="h-2.5 w-2.5" />
                              +{Number(bet.payout || Number(bet.sum || 0) * multiplier).toLocaleString()}
                            </motion.p>
                          )}

                          <AnimatedBetItems
                            betItems={betItems}
                            winSkin={bet.win_skin}
                            isWinner={isWinBet}
                            multiplier={multiplier}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Wheel500BetsFeed
